import { useState } from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { SEARCH_QUERIES, HUNT_PLATFORMS } from '../data/content';
import { Card, SectionLabel, Badge } from '../components/UI';

function SearchTag({ label }) {
  const [copied, setCopied] = useState(false);
  const twitterUrl = `https://twitter.com/search?q=${encodeURIComponent(label)}&f=live`;

  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(label).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'var(--bg3)', border: '1px solid var(--border2)',
      borderRadius: 8, padding: '6px 12px', margin: '4px',
      fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text2)',
      transition: 'all 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--text)'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)'; }}
    >
      {label}
      <button onClick={copy} style={{ background: 'none', border: 'none', color: 'inherit', padding: 0, cursor: 'pointer', display: 'flex' }}>
        {copied ? <Check size={12} color="var(--accent)" /> : <Copy size={12} />}
      </button>
      <a href={twitterUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit', display: 'flex' }}>
        <ExternalLink size={12} />
      </a>
    </div>
  );
}

const categories = [
  { key: 'hot', label: 'Hot intent', color: 'red' },
  { key: 'hiring', label: 'Hiring signals', color: 'accent' },
  { key: 'launch', label: 'Launch signals', color: 'amber' },
  { key: 'collab', label: 'Collab signals', color: 'blue' },
];

export default function HuntPage() {
  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.03em', marginBottom: 6 }}>Hunt</h1>
        <p style={{ color: 'var(--text2)', fontSize: 14 }}>Find active Web3 projects ready to spend on marketing. Filter by Latest on X.</p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <SectionLabel>X / Twitter search queries</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {categories.map(cat => (
            <Card key={cat.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Badge color={cat.color}>{cat.label}</Badge>
              </div>
              <div style={{ lineHeight: 2 }}>
                {SEARCH_QUERIES.filter(q => q.category === cat.key).map(q => (
                  <SearchTag key={q.label} label={q.label} />
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <SectionLabel>How to find the founder's DM</SectionLabel>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { step: '01', title: 'Find the project tweet', desc: 'A project announcing a launch, mint, or airdrop in the last 24h' },
              { step: '02', title: 'Check who posted it', desc: 'If it\'s the official account, look for a "team" page or "@founder" mention in bio' },
              { step: '03', title: 'Go to their replies', desc: 'Founders almost always reply to comments on their own launch tweets — that\'s your opening' },
              { step: '04', title: 'Check Discord / Telegram in bio', desc: 'If DMs are locked, go to their Discord and DM the founder there — often easier' },
              { step: '05', title: 'Find their personal account', desc: 'Look for "built by @name" in bio, or see who\'s replying from a personal handle' },
            ].map(({ step, title, desc }) => (
              <div key={step} style={{ display: 'flex', gap: 14 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', fontWeight: 500,
                }}>{step}</div>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div>
        <SectionLabel>Other hunting platforms</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          {HUNT_PLATFORMS.map(p => (
            <Card key={p.name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ fontWeight: 500, fontSize: 14, fontFamily: 'var(--font-display)' }}>{p.name}</div>
                <Badge color="accent">{p.speed}</Badge>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{p.where}</div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
